import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBolt,
  faBox,
  faCalendarDay,
  faDollarSign,
  faEye,
  faFilterCircleXmark,
  faFlag,
  faHeart,
  faInbox,
  faList,
  faMagnifyingGlass,
  faMap,
  faMapLocationDot,
  faMapMarkerAlt,
  faPlus,
  faPaperPlane,
  faRefresh,
  faRoute,
  faShieldHalved,
  faSliders,
  faStopwatch,
  faTableCells,
  faTruck,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { GoogleMapComponent } from '../../shared/google-map/google-map.component';
import { LoadRequestService } from '../load-request.service';
import { Observable, tap } from 'rxjs';
import { Page } from '../../models/page.model';
import {
  LoadRequest,
  LoadRequestFilterOptions,
  LoadRequestSearchParams,
  MarketplaceFilterOption
} from '../load-request.model';
import { MarketplaceAnalyticsService, HotLane, MarketplaceTrends } from '../marketplace-analytics.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { Paginator } from 'primeng/paginator';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LanguageService } from '../../services/language.service';
import { CurrencyResolverService } from '../../services/currency-resolver.service';

type ViewMode = 'list' | 'map';
type MapMode = 'loads' | 'trucks';
type SortOption = 'latest' | 'bestMatch' | 'rate' | 'distance';

type RequirementKey = 'temperatureControlled' | 'hazmat' | 'team';
type SelectOption = { label: string; value: string };
type SearchSummaryItem = { label: string; value: string };



@Component({
  selector: 'app-loads-list',
  standalone: true,
  templateUrl: './loads-list.component.html',
  styleUrls: ['./loads-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    FloatLabelModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    DatePicker,
    TooltipModule,
    DrawerModule,
    DialogModule,
    GoogleMapComponent,
    TranslateModule,
    FontAwesomeModule,
    DividerModule,
    Paginator,
    ToggleSwitch,
    FormsModule
  ]
})
export class LoadsListComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loadRequestService = inject(LoadRequestService);
  private readonly analyticsService = inject(MarketplaceAnalyticsService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly currencyResolver = inject(CurrencyResolverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly languageService = inject(LanguageService);

  readonly icons = {
    bolt: faBolt,
    box: faBox,
    calendar: faCalendarDay,
    dollar: faDollarSign,
    eye: faEye,
    filterClear: faFilterCircleXmark,
    flag: faFlag,
    heart: faHeart,
    inbox: faInbox,
    list: faList,
    map: faMap,
    mapLocation: faMapLocationDot,
    marker: faMapMarkerAlt,
    plus: faPlus,
    send: faPaperPlane,
    refresh: faRefresh,
    route: faRoute,
    search: faMagnifyingGlass,
    shield: faShieldHalved,
    sliders: faSliders,
    stopwatch: faStopwatch,
    table: faTableCells,
    truck: faTruck,
    user: faUser
  };

  sidebarVisible = false;
  advancedSearchVisible = false;
  filterOptionsLoading = false;
  useShipperFilter = false;
  useMyLoadsFilter = false;
  pageNumber = 0;
  pageSize = 10;

  loadRequests$!: Observable<Page<LoadRequest>>;
  hotLanes$!: Observable<HotLane[]>;
  trends$!: Observable<MarketplaceTrends>;

  readonly filtersForm: FormGroup = this.fb.group({
    searchText: [''],
    referenceNumber: [''],
    customer: [null],
    client: [null],
    pickupLocation: [''],
    deliveryLocation: [''],
    pickupDate: [''],
    pickupDateTo: [''],
    createdAtFrom: [''],
    createdAtTo: [''],
    readyTime: [''],
    equipment: [null],
    equipmentCount: [null],
    commodity: [null],
    paymentTerms: [null],
    currencyCode: [null],
    rate: [''],
    maxRate: [''],
    weight: [''],
    length: [null],
    palletCount: [null],
    status: [null],
    temperatureControlled: [false],
    hazmat: [false],
    team: [false],
    matchPreference: ['Best Match']
  });

  equipmentOptions: SelectOption[] = [];
  customerOptions: SelectOption[] = [];
  clientOptions: SelectOption[] = [];
  originOptions: SelectOption[] = [];
  destinationOptions: SelectOption[] = [];
  commodityOptions: SelectOption[] = [];
  paymentTermsOptions: SelectOption[] = [];
  currencyOptions: SelectOption[] = [];

  readonly lengthOptions = [
    { label: 'Up to 45 ft', value: 'Up to 45 ft' },
    { label: 'Up to 48 ft', value: 'Up to 48 ft' },
    { label: 'Up to 53 ft', value: 'Up to 53 ft' }
  ];

  statusOptions: SelectOption[] = [];



  readonly sortOptions: { label: string; value: SortOption }[] = [
    { label: 'Latest', value: 'latest' },
    { label: 'Best Match', value: 'bestMatch' },
    { label: 'Best Rate', value: 'rate' },
    { label: 'Shortest Distance', value: 'distance' }
  ];

  viewMode: ViewMode = 'list';
  mapMode: MapMode = 'loads';
  sortOption: SortOption = 'latest';
  selectedMapLoadId: number | null = null;
  private initialized = false;
  private readonly filterStorageKey = 'loadgate:private-loads:filters:v1';
  private readonly persistedDateFields = ['pickupDate', 'pickupDateTo', 'createdAtFrom', 'createdAtTo'];
  private suppressFilterPersistence = false;
  private pendingScrollRestore: number | null = null;

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      if (!this.initialized) {
        return;
      }
      this.loadFilterOptions();
      this.findLoads();
      this.fetchAnalytics();
    });
  }

  ngOnInit(): void {
    this.restoreFilterState();
    this.initialized = true;
    this.loadFilterOptions();
    this.findLoads();
    this.fetchAnalytics();
  }

  fetchAnalytics(): void {
    const shipperCompanyId = this.getActiveShipperCompanyId();
    const lang = this.languageService.currentLanguage();
    this.hotLanes$ = this.analyticsService.getHotLanes(5, shipperCompanyId, lang);
    this.trends$ = this.analyticsService.getTrends(5, shipperCompanyId, lang);
  }

  findLoads(): void {
    this.persistFilterState();
    const val = this.filtersForm.value;
    const params: LoadRequestSearchParams = {};

    if (this.cleanText(val.searchText)) params.searchText = this.cleanText(val.searchText);
    if (this.cleanText(val.referenceNumber)) params.referenceNumber = this.cleanText(val.referenceNumber);
    if (this.cleanText(val.customer)) params.contactName = this.cleanText(val.customer);
    if (this.cleanText(val.client)) params.clientCompanyName = this.cleanText(val.client);
    if (this.cleanText(val.pickupLocation)) params.origin = this.cleanText(val.pickupLocation);
    if (this.cleanText(val.deliveryLocation)) params.destination = this.cleanText(val.deliveryLocation);
    if (val.pickupDate) params.pickupDateFrom = this.formatDate(val.pickupDate);
    if (val.pickupDateTo) params.pickupDateTo = this.formatDate(val.pickupDateTo);
    if (val.createdAtFrom) params.createdAtFrom = this.formatDate(val.createdAtFrom);
    if (val.createdAtTo) params.createdAtTo = this.formatDate(val.createdAtTo);
    if (val.equipment) params.equipmentType = val.equipment;
    if (val.equipmentCount !== null && val.equipmentCount !== undefined) {
      params.equipmentCount = Number(val.equipmentCount);
    }
    if (this.cleanText(val.commodity)) params.commodity = this.cleanText(val.commodity);
    if (this.cleanText(val.paymentTerms)) params.paymentTerms = this.cleanText(val.paymentTerms);
    if (this.cleanText(val.currencyCode)) params.currencyCode = this.cleanText(val.currencyCode);
    if (val.status) params.status = val.status;
    if (val.rate) params.minTargetRate = Number(val.rate);
    if (val.maxRate) params.maxTargetRate = Number(val.maxRate);
    
    const shipperCompanyId = this.getActiveShipperCompanyId();
    if (shipperCompanyId) {
      params.shipperCompanyId = shipperCompanyId;
    }
    const createdBy = this.getActiveCreatedBy();
    if (createdBy) {
      params.createdBy = createdBy;
    }

    let sortStr = 'createdAt,desc';
    if (this.sortOption === 'rate') sortStr = 'targetRate,desc';
    if (this.sortOption === 'distance') sortStr = 'totalDistanceInKm,asc';

    params.sort = sortStr;
    params.lang = this.languageService.currentLanguage();

    this.loadRequests$ = this.loadRequestService.search(params, this.pageNumber, this.pageSize).pipe(
      tap(() => this.restoreScrollPositionOnce())
    );
  }

  onPageChange(event: any): void {
    this.pageNumber = event.page;
    this.pageSize = event.rows;
    this.pendingScrollRestore = 0;
    this.findLoads();
  }

  toggleShipperFilter(event: any): void {
    this.useShipperFilter = event.checked;
    this.pageNumber = 0;
    this.selectedMapLoadId = null;
    this.persistFilterState();
    this.findLoads();
    this.fetchAnalytics();
  }

  toggleMyLoadsFilter(): void {
    this.useMyLoadsFilter = !this.useMyLoadsFilter;
    this.pageNumber = 0;
    this.selectedMapLoadId = null;
    this.persistFilterState();
    this.findLoads();
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  setSort(option: SortOption): void {
    this.sortOption = option;
    this.pageNumber = 0;
    this.findLoads();
  }

  openAdvancedSearch(): void {
    this.advancedSearchVisible = true;
  }

  createLoad(): void {
    this.router.navigate(['/load/create']);
  }

  toggleRequirement(key: string): void {
    const rKey = key as RequirementKey;
    const current = this.filtersForm.get(rKey)?.value ?? false;
    this.filtersForm.patchValue({ [rKey]: !current });
  }

  clearFilters(): void {
    this.clearPersistedFilterState();
    this.suppressFilterPersistence = true;
    this.filtersForm.reset({
      pickupLocation: '',
      deliveryLocation: '',
      pickupDate: '',
      pickupDateTo: '',
      createdAtFrom: '',
      createdAtTo: '',
      readyTime: '',
      searchText: '',
      referenceNumber: '',
      customer: null,
      client: null,
      equipment: null,
      equipmentCount: null,
      commodity: null,
      paymentTerms: null,
      currencyCode: null,
      rate: '',
      maxRate: '',
      weight: '',
      length: null,
      palletCount: null,
      status: null,
      temperatureControlled: false,
      hazmat: false,
      team: false,
      matchPreference: 'Best Match'
    });

    this.pageNumber = 0;
    this.selectedMapLoadId = null;
    this.findLoads();
    this.suppressFilterPersistence = false;
  }

  applyFilters(): void {
    this.pageNumber = 0;
    this.selectedMapLoadId = null;
    this.pendingScrollRestore = 0;
    this.findLoads();
  }

  applyAdvancedFilters(): void {
    this.applyFilters();
    this.advancedSearchVisible = false;
  }

  refreshFilterOptions(): void {
    this.loadFilterOptions();
  }

  activeSearchSummary(): SearchSummaryItem[] {
    const val = this.filtersForm.value;
    const items: SearchSummaryItem[] = [];

    this.addSummaryItem(items, this.ui('Search', 'بحث'), this.cleanText(val.searchText));
    this.addSummaryItem(items, this.ui('Reference', 'المرجع'), this.cleanText(val.referenceNumber));
    this.addSummaryItem(items, this.ui('Customer', 'العميل'), this.cleanText(val.customer));
    this.addSummaryItem(items, this.ui('Client', 'الشركة'), this.cleanText(val.client));
    this.addSummaryItem(items, this.ui('Origin', 'مصدر الاستلام'), this.cleanText(val.pickupLocation));
    this.addSummaryItem(items, this.ui('Destination', 'وجهة التسليم'), this.cleanText(val.deliveryLocation));
    this.addSummaryItem(items, this.ui('Equipment', 'نوع المعدات'), this.cleanText(val.equipment));
    this.addSummaryItem(items, this.ui('Commodity', 'البضاعة'), this.cleanText(val.commodity));
    this.addSummaryItem(items, this.ui('Payment', 'الدفع'), this.cleanText(val.paymentTerms));
    this.addSummaryItem(items, this.ui('Currency', 'العملة'), this.cleanText(val.currencyCode));
    this.addSummaryItem(items, this.ui('Status', 'الحالة'), this.cleanText(val.status));
    this.addSummaryItem(items, this.ui('Pickup From', 'الاستلام من'), this.displayDate(val.pickupDate));
    this.addSummaryItem(items, this.ui('Pickup To', 'الاستلام إلى'), this.displayDate(val.pickupDateTo));
    this.addSummaryItem(items, this.ui('Created From', 'أنشئ من'), this.displayDate(val.createdAtFrom));
    this.addSummaryItem(items, this.ui('Created To', 'أنشئ إلى'), this.displayDate(val.createdAtTo));
    this.addSummaryItem(items, this.ui('Min Rate', 'أقل سعر'), this.displayNumber(val.rate));
    this.addSummaryItem(items, this.ui('Max Rate', 'أعلى سعر'), this.displayNumber(val.maxRate));
    this.addSummaryItem(items, this.ui('Equipment Count', 'عدد المعدات'), this.displayNumber(val.equipmentCount));

    if (this.useShipperFilter) {
      items.push({ label: this.ui('Scope', 'النطاق'), value: this.ui('My Company', 'شركتي') });
    }
    if (this.useMyLoadsFilter) {
      items.push({ label: this.ui('Owner', 'المالك'), value: this.ui('My Loads', 'أحمالي') });
    }

    return items;
  }

  switchMapMode(mode: MapMode): void {
    this.mapMode = mode;
  }

  openLoad(loadId: number): void {
    this.persistFilterState();
    this.router.navigate(['/load-details', loadId]);
  }

  signalBadges(load: LoadRequest): Array<{ icon: any; label: string; strong?: boolean }> {
    const signals = load.signals;
    const badges: Array<{ icon: any; label: string; strong?: boolean }> = [
      { icon: this.icons.calendar, label: this.ui(`Posted ${this.formatRelativeTime(load.createdAt)}`, `نشر ${this.formatRelativeTime(load.createdAt)}`) }
    ];
    if (!signals) {
      return badges;
    }
    if ((signals.viewCount || 0) > 0) {
      badges.push({ icon: this.icons.eye, label: this.ui(`Viewed ${signals.viewCount} times`, `شوهد ${signals.viewCount} مرة`) });
    }
    if ((signals.offersSubmittedCount || 0) > 0) {
      badges.push({ icon: this.icons.send, label: this.ui(`${signals.offersSubmittedCount} offers`, `${signals.offersSubmittedCount} عروض`) });
    }
    if ((signals.interestedUsersCount || 0) > 0) {
      badges.push({ icon: this.icons.user, label: this.ui(`${signals.interestedUsersCount} interested`, `${signals.interestedUsersCount} مهتم`) });
    }
    if (signals.hotLane) {
      badges.push({ icon: this.icons.bolt, label: this.ui('Hot lane', 'مسار نشط'), strong: true });
    }
    if (signals.fastPickup) {
      badges.push({ icon: this.icons.stopwatch, label: this.ui('Fast pickup', 'استلام سريع'), strong: true });
    }
    if (signals.verifiedShipper) {
      badges.push({ icon: this.icons.shield, label: this.ui('Verified shipper', 'شاحن موثق'), strong: true });
    }
    return badges;
  }

  ui(en: string, ar: string): string {
    return this.isArabic() ? ar : en;
  }

  isArabic(): boolean {
    return this.languageService.currentLanguage() === 'ar';
  }

  statusLabel(value?: string | null): string {
    const key = this.normalizeEnumKey(value);
    const ar: Record<string, string> = {
      ACTIVE: 'نشط',
      POSTED: 'منشور',
      BOOKED: 'محجوز',
      CANCELLED: 'ملغي',
      EXPIRED: 'منتهي'
    };
    return this.isArabic() ? (ar[key] ?? this.labelize(value)) : (value || 'POSTED');
  }

  labelize(value?: string | null): string {
    if (!value) {
      return '';
    }
    if (this.isArabic()) {
      const ar = this.arabicEnumLabels()[this.normalizeEnumKey(value)];
      if (ar) {
        return ar;
      }
    }
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  formatPallets(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return this.ui(`${value} pallets`, `${value} منصة`);
  }

  formatWeightLbs(value: number | null | undefined): string {
    if (value == null) {
      return '';
    }
    return this.ui(`${value.toLocaleString()} lb`, `${value.toLocaleString()} رطل`);
  }

  formatTargetRate(amount: number | null | undefined, code?: string | null): string {
    if (amount == null || amount <= 0) {
      return '';
    }
    return this.currencyResolver.formatCurrency(amount, {
      currencyCode: code,
      locale: this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined,
      maximumFractionDigits: 0
    });
  }

  formatDisplayDate(value?: string | null, includeWeekday = true): string {
    if (!value) {
      return this.ui('N/A', 'غير متوفر');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, {
      weekday: includeWeekday ? 'short' : undefined,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatHotLaneRoute(lane: HotLane): string {
    const origin = this.formatAnalyticsLabel(lane.origin);
    const destination = this.formatAnalyticsLabel(lane.destination);
    return this.isArabic() ? `${origin} إلى ${destination}` : `${origin} \u2192 ${destination}`;
  }

  formatHotLaneActivity(count: number | null | undefined): string {
    const value = this.formatLocalizedNumber(count ?? 0);
    return this.isArabic() ? `${value} نشاط أحمال` : `${value} loads activity`;
  }

  formatTrendLabel(value?: string | null): string {
    return this.formatAnalyticsLabel(value);
  }

  formatTrendTooltip(count: number | null | undefined): string {
    const value = this.formatLocalizedNumber(count ?? 0);
    return this.isArabic() ? `${value} طلبات` : `${value} requests`;
  }

  formatAverageWeight(value: number | null | undefined): string {
    const amount = this.formatLocalizedNumber(value ?? 0, '1.0-0');
    return this.isArabic() ? `${amount} رطل` : `${amount} lb`;
  }

  formatAveragePallets(value: number | null | undefined): string {
    return this.formatLocalizedNumber(value ?? 0, '1.0-1');
  }

  selectMapLoad(load: LoadRequest): void {
    this.selectedMapLoadId = load.id;
  }

  selectedMapLoad(page: Page<LoadRequest> | null | undefined): LoadRequest | null {
    const loads = page?.content ?? [];
    return loads.find(load => load.id === this.selectedMapLoadId) ?? loads[0] ?? null;
  }

  onLocationOptionFilter(type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity', event: unknown): void {
    const term = this.extractFilterTerm(event);
    if (!term) {
      this.setLazyOptions(type, []);
      return;
    }

    const params = this.lazyOptionParams(type, term);

    this.loadRequestService
      .getFilterOptions({ ...params, lang: this.languageService.currentLanguage() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: options => {
          const values =
            type === 'origin'
              ? options.origins
              : type === 'destination'
                ? options.destinations
                : type === 'equipment'
                  ? options.equipmentTypes
                  : type === 'paymentTerms'
                    ? options.paymentTerms
                    : options.commodities;
          const displayOptions =
            type === 'origin'
              ? options.originOptions
              : type === 'destination'
                ? options.destinationOptions
                : type === 'equipment'
                  ? options.equipmentTypeOptions
                  : type === 'paymentTerms'
                    ? options.paymentTermOptions
                    : options.commodityOptions;
          this.setLazyOptions(type, this.toSelectOptions(displayOptions ?? values));
          this.cdr.markForCheck();
        },
        error: () => {
          this.setLazyOptions(type, []);
          this.cdr.markForCheck();
        }
      });
  }

  private cleanText(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length ? value.trim() : undefined;
  }

  private extractFilterTerm(event: unknown): string {
    const filter = (event as { filter?: unknown })?.filter;
    return typeof filter === 'string' ? filter.trim() : '';
  }

  private lazyOptionParams(
    type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity',
    term: string
  ): { origin?: string; destination?: string; equipmentType?: string; paymentTerms?: string; commodity?: string } {
    if (type === 'origin') return { origin: term };
    if (type === 'destination') return { destination: term };
    if (type === 'equipment') return { equipmentType: term };
    if (type === 'paymentTerms') return { paymentTerms: term };
    return { commodity: term };
  }

  private setLazyOptions(
    type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity',
    options: SelectOption[]
  ): void {
    if (type === 'origin') {
      this.originOptions = options;
      return;
    }

    if (type === 'destination') {
      this.destinationOptions = options;
      return;
    }

    if (type === 'equipment') {
      this.equipmentOptions = options;
      return;
    }

    if (type === 'paymentTerms') {
      this.paymentTermsOptions = options;
      return;
    }

    this.commodityOptions = options;
  }

  private formatDate(value: unknown): string | undefined {
    if (!(value instanceof Date)) {
      return typeof value === 'string' && value.trim().length ? value : undefined;
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatRelativeTime(value?: string | null): string {
    if (!value) {
      return this.ui('recently', 'مؤخراً');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.ui('recently', 'مؤخراً');
    }
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return this.ui('just now', 'الآن');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return this.ui(`${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`, `قبل ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.ui(`${hours} ${hours === 1 ? 'hour' : 'hours'} ago`, `قبل ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`);
    const days = Math.floor(hours / 24);
    if (days < 30) return this.ui(`${days} ${days === 1 ? 'day' : 'days'} ago`, `قبل ${days} ${days === 1 ? 'يوم' : 'أيام'}`);
    const months = Math.floor(days / 30);
    if (months < 12) return this.ui(`${months} ${months === 1 ? 'month' : 'months'} ago`, `قبل ${months} ${months === 1 ? 'شهر' : 'أشهر'}`);
    const years = Math.floor(months / 12);
    return this.ui(`${years} ${years === 1 ? 'year' : 'years'} ago`, `قبل ${years} ${years === 1 ? 'سنة' : 'سنوات'}`);
  }

  private displayDate(value: unknown): string | undefined {
    return this.formatDate(value);
  }

  private displayNumber(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    return String(value);
  }

  private formatLocalizedNumber(value: number, digits = '1.0-0'): string {
    const [integerPart, fractionPart = '0-0'] = digits.split('.');
    const [minimumFractionDigits, maximumFractionDigits] = fractionPart.split('-').map(part => Number(part));
    const minimumIntegerDigits = Number(integerPart) || 1;
    return new Intl.NumberFormat(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, {
      minimumIntegerDigits,
      minimumFractionDigits: Number.isFinite(minimumFractionDigits) ? minimumFractionDigits : 0,
      maximumFractionDigits: Number.isFinite(maximumFractionDigits) ? maximumFractionDigits : 0
    }).format(value);
  }

  private formatAnalyticsLabel(value?: string | null): string {
    const clean = (value ?? '').trim();
    if (!clean || this.isUnavailableValue(clean)) {
      return this.ui('N/A', 'غير متوفر');
    }
    if (!this.isArabic()) {
      return clean;
    }

    const mapped = this.arabicAnalyticsLabels()[this.normalizeEnumKey(clean)];
    if (mapped) {
      return mapped;
    }

    return clean
      .split(/[\s_/+-]+/)
      .filter(Boolean)
      .map(part => this.arabicAnalyticsLabels()[this.normalizeEnumKey(part)] ?? part)
      .join(' ');
  }

  private persistFilterState(): void {
    if (this.suppressFilterPersistence || typeof localStorage === 'undefined') {
      return;
    }

    const serialized = Object.fromEntries(
      Object.entries(this.filtersForm.value).map(([key, value]) => [key, this.serializeFilterValue(value)])
    );

    localStorage.setItem(this.filterStorageKey, JSON.stringify({
      filters: serialized,
      useShipperFilter: this.useShipperFilter,
      useMyLoadsFilter: this.useMyLoadsFilter,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      scrollY: this.pendingScrollRestore ?? this.currentScrollY()
    }));
  }

  private restoreFilterState(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const raw = localStorage.getItem(this.filterStorageKey);
    if (!raw) {
      return;
    }

    try {
      const saved = JSON.parse(raw) as {
        filters?: Record<string, unknown>;
        useShipperFilter?: boolean;
        useMyLoadsFilter?: boolean;
        pageNumber?: number;
        pageSize?: number;
        scrollY?: number;
      };
      const restored = Object.fromEntries(
        Object.entries(saved.filters ?? {}).map(([key, value]) => [key, this.deserializeFilterValue(key, value)])
      );

      this.filtersForm.patchValue(restored, { emitEvent: false });
      this.useShipperFilter = !!saved.useShipperFilter;
      this.useMyLoadsFilter = !!saved.useMyLoadsFilter;
      this.pageNumber = Number.isFinite(saved.pageNumber) ? Number(saved.pageNumber) : this.pageNumber;
      this.pageSize = Number.isFinite(saved.pageSize) ? Number(saved.pageSize) : this.pageSize;
      this.pendingScrollRestore = Number.isFinite(saved.scrollY) ? Number(saved.scrollY) : null;
      this.seedPersistedSelectOptions(restored);
    } catch {
      localStorage.removeItem(this.filterStorageKey);
    }
  }

  private clearPersistedFilterState(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.filterStorageKey);
    }
  }

  private serializeFilterValue(value: unknown): unknown {
    return value instanceof Date ? this.formatDate(value) : value;
  }

  private deserializeFilterValue(key: string, value: unknown): unknown {
    if (!this.persistedDateFields.includes(key) || typeof value !== 'string' || !value) {
      return value;
    }

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date;
  }

  private seedPersistedSelectOptions(restored: Record<string, unknown>): void {
    this.originOptions = this.seedOption(this.originOptions, restored['pickupLocation']);
    this.destinationOptions = this.seedOption(this.destinationOptions, restored['deliveryLocation']);
    this.equipmentOptions = this.seedOption(this.equipmentOptions, restored['equipment']);
    this.commodityOptions = this.seedOption(this.commodityOptions, restored['commodity']);
    this.paymentTermsOptions = this.seedOption(this.paymentTermsOptions, restored['paymentTerms']);
  }

  private seedOption(options: SelectOption[], value: unknown): SelectOption[] {
    if (typeof value !== 'string' || !value.trim() || options.some(option => option.value === value)) {
      return options;
    }
    return [{ label: value, value }, ...options];
  }

  private restoreScrollPositionOnce(): void {
    const scrollY = this.pendingScrollRestore;
    if (scrollY === null || typeof window === 'undefined') {
      return;
    }

    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'auto' });
      this.pendingScrollRestore = null;
    });
  }

  private currentScrollY(): number {
    return typeof window === 'undefined' ? 0 : window.scrollY || 0;
  }

  private isUnavailableValue(value: string): boolean {
    return ['N/A', 'NA', 'NONE', 'NULL', 'UNKNOWN', 'غير متوفر'].includes(value.trim().toUpperCase());
  }

  private normalizeEnumKey(value?: string | null): string {
    return (value ?? '')
      .trim()
      .replace(/[-\s]+/g, '_')
      .replace(/:+/g, '')
      .toUpperCase();
  }

  private arabicEnumLabels(): Record<string, string> {
    return {
      UNVERIFIED: 'غير موثق',
      VERIFIED: 'موثق',
      PENDING: 'قيد المراجعة',
      LIMITED_HISTORY: 'سجل محدود',
      COMMUNICATION_ONLY: 'تواصل فقط',
      PAYMENT_LIMITED_HISTORY: 'الدفع: سجل محدود',
      NEW_UNVERIFIED_PROFILE: 'ملف جديد/غير موثق',
      MARKETPLACE_LEAD: 'عميل من السوق',
      TRUST_PROFILE_NOT_AVAILABLE: 'ملف الثقة غير متاح',
      REVIEWED_TRUST_PROFILE: 'ملف ثقة تمت مراجعته',
      MOBILE_VERIFIED: 'الجوال موثق',
      COMPANY_PROFILE: 'ملف الشركة',
      ACTIVE: 'نشط',
      POSTED: 'منشور',
      BOOKED: 'محجوز'
    };
  }

  private arabicAnalyticsLabels(): Record<string, string> {
    return {
      N_A: 'غير متوفر',
      HEAVY_TRUCK: 'شاحنة ثقيلة',
      HEAVY_TRUCK_FLATBED: 'شاحنة ثقيلة سطحة',
      HEAVY_TRUCK_REFRIGERATED: 'شاحنة ثقيلة مبردة',
      HEAVY_TRUCK_CURTAIN_SIDER: 'شاحنة ثقيلة بجوانب ستائرية',
      HEAVY_TRUCK_CURTAIN_SIDE: 'شاحنة ثقيلة بجوانب ستائرية',
      HEAVY: 'ثقيلة',
      TRUCK: 'شاحنة',
      FLATBED: 'سطحة',
      REFRIGERATED: 'مبردة',
      CURTAIN_SIDER: 'جوانب ستائرية',
      CURTAIN_SIDE: 'جوانب ستائرية',
      CURTAIN: 'ستائرية',
      SIDER: 'جوانب',
      VAN: 'فان',
      BOX: 'صندوق',
      DRY_VAN: 'فان جاف',
      LOWBED: 'لوبد',
      TANKER: 'صهريج',
      CONTAINER: 'حاوية',
      TRAILER: 'مقطورة'
    };
  }

  private addSummaryItem(items: SearchSummaryItem[], label: string, value: string | undefined): void {
    if (!value) {
      return;
    }

    items.push({ label, value });
  }

  private getActiveShipperCompanyId(): number | undefined {
    if (!this.useShipperFilter) {
      return undefined;
    }

    return this.sessionStorageService.getCompany()?.id;
  }

  private getActiveCreatedBy(): number | undefined {
    if (!this.useMyLoadsFilter) {
      return undefined;
    }

    const user = this.sessionStorageService.getUser();
    return user?.id ?? user?.rowId ?? undefined;
  }

  private loadFilterOptions(): void {
    this.filterOptionsLoading = true;
    this.loadRequestService
      .getFilterOptions({ lang: this.languageService.currentLanguage() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: options => {
          this.applyFilterOptions(options);
          this.filterOptionsLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.filterOptionsLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private applyFilterOptions(options: LoadRequestFilterOptions): void {
    this.customerOptions = this.toSelectOptions(options.contactNames);
    this.clientOptions = this.toSelectOptions(options.clientCompanyNames);
    this.originOptions = this.toSelectOptions(options.originOptions ?? options.origins);
    this.destinationOptions = this.toSelectOptions(options.destinationOptions ?? options.destinations);
    this.equipmentOptions = this.toSelectOptions(options.equipmentTypeOptions ?? options.equipmentTypes);
    this.commodityOptions = this.toSelectOptions(options.commodityOptions ?? options.commodities);
    this.paymentTermsOptions = this.toSelectOptions(options.paymentTermOptions ?? options.paymentTerms);
    this.currencyOptions = this.toSelectOptions(options.currencyCodes);
    this.statusOptions = this.toSelectOptions(options.statuses);
  }

  private toSelectOptions(values: Array<string | MarketplaceFilterOption> | null | undefined): SelectOption[] {
    const seen = new Set<string>();
    return (values ?? [])
      .map(value => (typeof value === 'string' ? { label: value, value } : value))
      .filter(option => option?.value?.trim())
      .filter(option => {
        const key = option.value.trim();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map(option => ({ label: option.label?.trim() || option.value.trim(), value: option.value.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}
